import { Project, Distribution, Setting, defaultPieceRoles } from "./state"
import { registeredSchemaNames, schemaLabel, blockFinishedInches } from "./blockschema"
import { distributionAxisSwapping, distributionValues } from "./layout"
import type { ToolName } from "../tool-schemas"

export type DimensionId = string

export interface DimensionValue {
  value: unknown
  label: string
  swatches?: string[]
  // When set and returns a non-null string, the menu renders this value as
  // disabled with the returned reason inline. Used for value-level applicability
  // (e.g. axis-swapping distributions on non-square blocks) — the parallel of
  // Dimension.applicability for whole-dim gating.
  disabledReason?: (p: Project) => string | null
}

// The tool call this dimension synthesizes when a value is chosen from the
// menu. The menu tap path runs this through executeTool exactly the way the
// LLM would, so the project mutation, transcript event, and inline
// visualization all flow through the existing tool machinery — no second
// path for "menu does it" vs. "the model does it."
//
// Optional `chain` runs follow-up tool calls in the same synthesized turn —
// each sees the project state mutated by the previous call. Block dimensions
// chain `render_block` so a tap shows the block immediately, mirroring the
// LOOM_004 swatch row's colorway_set + render_block pattern.
export interface DimensionToolCall {
  name: ToolName
  args: Record<string, unknown>
  chain?: Array<{ name: ToolName; args: Record<string, unknown> }>
}

export interface Dimension {
  id: DimensionId
  label: string
  category: "block" | "layout" | "palette" | "color" | "fabric"
  layer: "concrete" | "meta"
  values?: DimensionValue[]
  range?: [number, number]
  applicability?: (p: Project) => boolean
  // Concrete dimensions read + write a single project field. Meta dimensions
  // (e.g. layout.shape) compute a bundle of edits and have no canonical
  // current value, so they omit `read` and the menu shows all options.
  read?: (p: Project) => unknown
  apply: (p: Project, value: unknown) => Project
  // The tool this dimension is the menu-side mirror of. Returning the same
  // {name, args} for a value as the LLM would emit means a tap synthesizes
  // a transcript event indistinguishable from a model-driven call.
  tool: (value: unknown) => DimensionToolCall
}

const BLOCK_STYLE_DIMENSION: Dimension = {
  id: "block.style",
  label: "Block style",
  category: "block",
  layer: "concrete",

  values: registeredSchemaNames().map(key => ({
    value: key,
    label: schemaLabel(key),
  })),
  read: (p) => p.block.style,
  apply: (p, value) => {
    const style = value as string
    return { ...p, block: { ...p.block, style, piece_roles: defaultPieceRoles(style) } }
  },
  tool: (value) => ({
    name: "block_set",
    args: { style: value as string },
    chain: [{ name: "render_block", args: {} }],
  }),
}

// Hint shown when a 90°/270° rotation distribution is paired with a non-square
// block. These distributions swap the block's W and H per rotated cell, so a
// 6×4 Rail Fence block doesn't tile cleanly. Suggest the square sibling schema
// (Classic Rail Fence) as the canonical fix.
const NON_SQUARE_ROTATION_HINT = "needs a square block (try Classic Rail Fence)"

const LAYOUT_DISTRIBUTION_DIMENSION: Dimension = {
  id: "layout.distribution",
  label: "Layout distribution",
  category: "layout",
  layer: "concrete",

  values: distributionValues().map((v) =>
    distributionAxisSwapping(v.value)
      ? {
          ...v,
          disabledReason: (p) => {
            const [w, h] = blockFinishedInches(p.block)
            return w === h ? null : NON_SQUARE_ROTATION_HINT
          },
        }
      : v
  ),
  applicability: (p) => p.layout.rows > 0 && p.layout.cols > 0,
  read: (p) => p.layout.distribution,
  apply: (p, value) => ({ ...p, layout: { ...p.layout, distribution: value as Distribution } }),
  tool: (value) => ({
    name: "layout_set",
    args: { distribution: value as Distribution },
    chain: [{ name: "render_quilt", args: {} }],
  }),
}

const LAYOUT_SETTING_DIMENSION: Dimension = {
  id: "layout.setting",
  label: "Setting",
  category: "layout",
  layer: "concrete",

  values: [
    { value: "straight" as Setting, label: "Straight Set" },
    { value: "on_point" as Setting, label: "On-Point" },
  ],
  applicability: (p) => {
    const [w, h] = blockFinishedInches(p.block)
    return w === h && p.layout.rows > 0 && p.layout.cols > 0
  },
  read: (p) => p.layout.setting ?? "straight",
  // Carries (rows, cols) verbatim across the flip — same block grid,
  // straight rotates to a square-AABB diamond under on_point. Future:
  // recompute borders on flip to preserve finished blanket dimensions
  // (REC_007 step 2B).
  apply: (p, value) => ({ ...p, layout: { ...p.layout, setting: value as Setting } }),
  tool: (value) => ({
    name: "layout_set",
    args: { setting: value as Setting },
    chain: [{ name: "render_quilt", args: {} }],
  }),
}

const BLOCK_UNIT_INCHES_DIMENSION: Dimension = {
  id: "block.unit_inches",
  label: "Block unit",
  category: "block",
  layer: "concrete",

  values: [
    { value: 1.5,  label: '1½"' },
    { value: 1.75, label: '1¾"' },
    { value: 2,    label: '2"' },
    { value: 2.5,  label: '2½"' },
    { value: 3,    label: '3"' },
    { value: 4,    label: '4"' },
  ],
  read: (p) => p.block.unit_inches,
  apply: (p, value) => ({ ...p, block: { ...p.block, unit_inches: value as number } }),
  tool: (value) => ({
    name: "block_set",
    args: { unit_inches: value as number },
    chain: [{ name: "render_block", args: {} }],
  }),
}

const LAYOUT_SASHING_DIMENSION: Dimension = {
  id: "layout.sashing_width",
  label: "Sashing",
  category: "layout",
  layer: "concrete",

  values: [
    { value: 0,   label: "No sashing" },
    { value: 1,   label: '1" sashing' },
    { value: 1.5, label: '1½" sashing' },
    { value: 2,   label: '2" sashing' },
    { value: 2.5, label: '2½" sashing' },
    { value: 3,   label: '3" sashing' },
  ],
  // On-point sashing isn't rendered — `computeQuiltLayout` only emits sash
  // rects in the straight-set branch, and the on-point sashing geometry
  // (rotated strips with bias edges) hasn't been built. Hide the dimension
  // when on-point so the user can't pay for sashing they won't see; the
  // cut planner short-circuits to null on the same condition. (LOOM_013 B.)
  applicability: (p) =>
    p.layout.rows > 0 && p.layout.cols > 0 && p.layout.setting !== "on_point",
  read: (p) => p.layout.sashing_width ?? 0,
  apply: (p, value) => ({ ...p, layout: { ...p.layout, sashing_width: value as number } }),
  tool: (value) => ({
    name: "sashing_set",
    args: { width_inches: value as number },
    chain: [{ name: "render_quilt", args: {} }],
  }),
}

// Meta dimension: resolves a named quilt size to rows × cols.
// Tuples are [rows, cols]; labels show cols×rows (W×H) per the app's display convention.
// Under setting === "on_point" the same tuple is the rotated block grid;
// the AABB is square (side = (rows+cols)*blockDiag/2) regardless of M, N,
// so picked tuples produce a roughly-square pieced blanket. Adding borders
// to fit a rectangular target is REC_007 step 2B.
const LAYOUT_SHAPE_DIMENSION: Dimension = {
  id: "layout.shape",
  label: "Quilt size",
  category: "layout",
  layer: "meta",

  values: [
    { value: [7, 5],   label: "Crib (5×7)" },
    { value: [6, 5],   label: "Throw (5×6)" },
    { value: [8, 6],   label: "Twin (6×8)" },
    { value: [8, 7],   label: "Full (7×8)" },
    { value: [9, 8],   label: "Queen (8×9)" },
    { value: [10, 10], label: "King (10×10)" },
  ],
  apply: (p, value) => {
    const [rows, cols] = value as [number, number]
    return { ...p, layout: { ...p.layout, rows, cols } }
  },
  tool: (value) => {
    const [rows, cols] = value as [number, number]
    return {
      name: "layout_set",
      args: { rows, cols },
      chain: [{ name: "render_quilt", args: {} }],
    }
  },
}

export const DIMENSIONS: Dimension[] = [
  BLOCK_STYLE_DIMENSION,
  BLOCK_UNIT_INCHES_DIMENSION,
  LAYOUT_SHAPE_DIMENSION,
  LAYOUT_DISTRIBUTION_DIMENSION,
  LAYOUT_SETTING_DIMENSION,
  LAYOUT_SASHING_DIMENSION,
]

// Returns the current value of this dimension for the given project.
// Meta dimensions (no `read`) return undefined so the menu shows all values.
export function currentValue(dim: Dimension, project: Project): unknown {
  return dim.read?.(project)
}
